import { ChangeEvent, ReactElement, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { T, TF } from "react-translator-component";
import imageCompression from "browser-image-compression";
import apiServices from "../../services/apiServices";
import LoadingModal from "../../../components/LoadingModal";
import FormDialog from "../FormDialog";
import CreateUpdateButton from "../CreateUpdateButton";
import { colors } from "../../../common/colors";
import { convertUserImage } from "../../../util/utils";
import profile from "../../../assets/images/profile_img.png";
import { adminRoles, MIN_PASSWORD_LENGTH } from "../../constants/adminUsers";
import { isSuperAdmin } from "../../util/util";
import { useAppDispatch } from "../../../redux/store";
import { setErrorObj } from "../../redux/errorReducer";
import { updateAdminUser } from "../../redux/adminAuthReducer";
import {
  Avatar, Button, Checkbox, FormControlLabel, Radio, RadioGroup, TextField
} from "@mui/material";

interface Props {
  show: boolean;
  editingUser?: any;
  onReload: () => void;
  onClose: () => void;
}

type Form = {
  name: string;
  nickname: string;
  email: string;
  password: string;
  image: string;
  role: string;
  isEnabled: boolean
}

export default function AdminUserFormModal({ show, editingUser = undefined, onReload, onClose }: Props): ReactElement {
  const initialErrors = {
    name: "",
    nickname: "",
    email: "",
    password: ""
  }

  const initialForm = {
    name: "",
    nickname: "",
    email: "",
    password: "",
    image: "",
    role: adminRoles.admin,
    isEnabled: true
  }

  const dispatch = useAppDispatch();
  const { admin } = useSelector((state: any) => state.adminAuth);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [form, setForm] = useState<Form>(initialForm);
  const [formErrors, setFormErrors] = useState<any>(initialErrors)

  useEffect(() => {
    setFormErrors(initialErrors)
    if (!show || !editingUser) {
      setForm(initialForm)
      return
    }

    const newForm = initialForm
    newForm.name = editingUser.name
    newForm.nickname = editingUser.nickname
    newForm.email = editingUser.email
    newForm.image = editingUser.image
    newForm.role = editingUser.role
    newForm.isEnabled = editingUser.disabled_at ? false : true

    setForm(newForm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingUser]);

  const handleChangeRole = (event: ChangeEvent<HTMLInputElement>) => {
    if(!isSuperAdmin(admin)) return

    const _role = (event.target as HTMLInputElement).value
    setForm({...form, role: _role})
  };

  const validHalfWidthCharacters = (text: string) => {
    const pattern = /^[a-zA-Z0-9]*$/g;
    return text.match(pattern) || !text
  }

  const onChangeFilterPassword = (event: any) => {
    const value = event.target.value;
    setForm({...form, password: value})

    if (validHalfWidthCharacters(value)) {
      setFormErrors({...formErrors, password: ""})
    } else {
      setFormErrors({...formErrors, password: T("mng_error_password_invalid_format")})
    }
  };

  const hasFormError = (obj: any): boolean => {
    return Object.values(obj).some((item: any) => item !== "")
  }

  const isValidForm = () => {
    const newErrors = initialErrors

    if (!form.name.trim()) newErrors.name = T("mng_error_name_blank")
    if (!form.nickname.trim()) newErrors.nickname = T("mng_error_nickname_blank")
    if (!form.email.trim()) newErrors.email = T("mng_error_email_blank")

    if (!form.password.trim()) {
      if (!editingUser) newErrors.password = T("mng_error_password_blank")
    } else {
      if (!validHalfWidthCharacters(form.password)) {
        newErrors.password = T("mng_error_password_invalid_format")
      } else if (form.password.length < MIN_PASSWORD_LENGTH) {
        newErrors.password = TF(T("mng_error_password_invalid_length"), MIN_PASSWORD_LENGTH)
      }
    }

    setFormErrors(newErrors)
    return hasFormError(newErrors)
  }

  const handleSubmit = async () => {
    if (isValidForm()) return false

    const params = {
      name: form.name,
      nickname: form.nickname,
      email: form.email,
      password: form.password,
      role: form.role,
      is_disabled: !form.isEnabled,
      image: form.image
    };

    try {
      setIsLoading(true);
      if (editingUser?.id) {
        const res = await apiServices.updateAdminUser(editingUser?.id, params)
        if (admin.id === editingUser?.id) dispatch(updateAdminUser(res))
      } else {
        await apiServices.createAdminUser(params)
      }
      onReload()
      return true
    } catch (error: any) {
      dispatch(setErrorObj(error))
      return false
    } finally {
      setIsLoading(false);
    }
  };

  const isValidSubmit = useMemo(() => {
    const isFormValid  = form.name.trim() && form.nickname.trim() && form.email.trim() && !hasFormError(formErrors)
    return editingUser ? isFormValid : isFormValid  && form.password.trim()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, formErrors])

  const handleEnableNavigator = () => {
    if (!isSuperAdmin(admin)) return

    if (form.role === adminRoles.navigator) setForm({...form, isEnabled: !form.isEnabled})
  }

  const displayDialogTitle = () => {
    return T(editingUser ? "mng_edit_admin_user" : "mng_create_admin_user")
  }

  const handleImageChange = async (e: any) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Define compression options
        const options = {
          maxSizeMB: 1, // (default: Number.POSITIVE_INFINITY)
          maxWidthOrHeight: 100, // (default: undefined)
          useWebWorker: true, // (default: true)
        };

        // Compress the file
        const compressedFile = await imageCompression(file, options);

        // Convert compressed file to base64
        const base64: any = await fileToBase64(compressedFile);
        const cleanBase64 = removeBase64Prefix(base64);

        setForm({...form, image: cleanBase64})
      } catch (error) {
        console.error("Error compressing the image:", error);
      }
    }
  };

  const removeBase64Prefix = (base64: string) => {
    return base64.replace(/^data:image\/[a-z]+;base64,/, "");
  };

  const fileToBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleChangeFormValue = (key: string, value: any) => {
    const newForm: any = Object.assign({}, JSON.parse(JSON.stringify(form)));
    newForm[key] = value
    setForm(newForm)

    const newErrors: any = Object.assign({}, JSON.parse(JSON.stringify(formErrors)));
    newErrors[key] = ""
    setFormErrors(newErrors)
  }

  return (
    <>
      <LoadingModal visible={isLoading} />

      <FormDialog open={show} onClose={onClose}>
        <div style={styles.container}>
          <span style={styles.title}>
            {displayDialogTitle()}
          </span>

          <Avatar src={form.image ? convertUserImage(form.image) : profile} style={styles.logo} />

          <div>
            <Button
              color={"secondary"}
              sx={styles.iconSettingBtn}
              component="label"
              variant="outlined"
            >
              {T("mng_admin_user_image_label")}
              <input type="file" name="image" accept="image/*" hidden onChange={handleImageChange} />
            </Button>

          </div>

          <div style={styles.section}>
            <div style={styles.nameField}>
              <span style={styles.fieldTitle}>
                {T(`mng_admin_user_name`)}{T(`mng_required`)}
              </span>

              <TextField
                placeholder={`${T(`mng_admin_user_name`)}`}
                size="small"
                value={form.name}
                onChange={(e: any) => handleChangeFormValue("name", e.target.value)}
                style={styles.textField}
              />
              {formErrors.name && (
                <span style={styles.noticeMessage}>
                  {formErrors.name}
                </span>
              )}
            </div>

            <div style={styles.nameField}>
              <span style={styles.fieldTitle}>
                {T(`mng_admin_user_nickname`)}{T(`mng_required`)}
              </span>

              <TextField
                placeholder={`${T(`mng_admin_user_nickname`)}`}
                size="small"
                value={form.nickname}
                onChange={(e: any) => handleChangeFormValue("nickname", e.target.value)}
                style={styles.textField}
              />
              {formErrors.nickname && (
                <span style={styles.noticeMessage}>
                  {formErrors.nickname}
                </span>
              )}
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.inputFields}>
              <span style={styles.fieldTitle}>
                {T(`mng_admin_user_email`)}{T(`mng_required`)}
              </span>

              <TextField
                placeholder={`${T(`mng_admin_user_email`)}`}
                size="small"
                style={styles.textField}
                value={form.email}
                onChange={(e: any) => handleChangeFormValue("email", e.target.value)}
              />
              {formErrors.email && (
                <span style={styles.noticeMessage}>
                  {formErrors.email}
                </span>
              )}
            </div>
          </div>

          <div style={styles.section}>
            <div style={styles.inputFields}>
              <span style={styles.fieldTitle}>
                {T("mng_admin_user_password_title")}{T(`mng_${editingUser ? "optional" : "required"}`)}
              </span>

              <TextField
                placeholder={`${T(`mng_${editingUser ? "edit" : "create"}_admin_user_password_placeholder`)}`}
                size="small"
                value={form.password}
                onChange={(e: any) => onChangeFilterPassword(e)}
                style={styles.textField}
              />
              {formErrors.password && (
                <span style={styles.noticeMessage}>
                  {formErrors.password}
                </span>
              )}
              {editingUser && form.password && !formErrors.password && (
                <span style={styles.noticeMessage}>
                  {T("mng_notice_change_password")}
                </span>
              )}
            </div>
          </div>

          <div style={styles.tableInputSec}>
            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={form.role}
              onChange={handleChangeRole}
              row
              style={styles.radioButton}
            >
              <FormControlLabel
                value={adminRoles.superAdmin}
                control={<Radio />}
                disabled={Boolean(editingUser) || !isSuperAdmin(admin)}
                label={`${T("mng_admin_user_super_admin")}`}
                style={styles.radioBtnLabelSuperAdmin}
              />
              <FormControlLabel
                value={adminRoles.admin}
                control={<Radio />}
                label={`${T("mng_admin_user_admin")}`}
                disabled={!isSuperAdmin(admin) || isSuperAdmin(editingUser)}
                style={styles.radioBtnLabelAdmin}
              />
              <FormControlLabel
                value={adminRoles.navigator}
                control={<Radio />}
                label={`${T("mng_admin_user_navigator")}`}
                disabled={!isSuperAdmin(admin) || isSuperAdmin(editingUser)}
                style={styles.radioBtnLabelNavigator}
              />
            </RadioGroup>

            <FormControlLabel
              control={<Checkbox checked={form.isEnabled} disabled={form.role !== adminRoles.navigator} />}
              label={`${T("mng_admin_user_navigator_enabled")}`}
              disabled={
                !isSuperAdmin(admin) ||
                isSuperAdmin(editingUser) ||
                form.role !== adminRoles.navigator
              }
              onClick={handleEnableNavigator}
              style={styles.naviEnabledCheck}
            />
          </div>

          <CreateUpdateButton
            title={editingUser === undefined ? T(`mng_create`) : T(`mng_update`)}
            validate={async () => !hasFormError(formErrors)}
            disabled={!isValidSubmit}
            onSubmit={handleSubmit}
          />
        </div>
      </FormDialog>
    </>
  );
}

const styles = {
  container: {
    display: "flex",
    paddingBottom: 20,
    alignItems: "center",
    justifyContent: "flex-start",
    alignContent: "center",
    flexDirection: "column" as const,
  },
  title: {
    fontSize: 20,
    alignSelf: "center",
    marginBottom: 10,
    fontWeight: "600",
    color: colors.farm_blue200,
  },
  section: {
    display: "flex",
    width: "90%",
    justifyContent: "space-between",
    marginTop: 10
  },
  nameField: { width: "48%" },
  fieldTitle: {
    fontSize: 14,
    color: colors.farm_blue200,
    marginBottom: 5,
    fontWeight: "500",
  },
  textField: { fontSize: 22, width: "100%" },
  inputFields: { width: "100%" },
  noticeMessage: {
    fontSize: 14,
    color: colors.farm_red600,
    marginBottom: 5,
    fontWeight: "500",
  },
  tableInputSec: {
    marginTop: 10,
    alignSelf: "center",
    alignContent: "center",
    width: "90%",
  },
  logo: {
    width: 70,
    height: 70,
    alignSelf: "center",
    marginBottom: 20,
  },
  radioButton: {
    width: "100%",
    marginTop: 20,
    displat: "flex",
  },
  radioBtnLabelSuperAdmin: { flex: 1.3, margin: "0" },
  radioBtnLabelAdmin: { flex: 1, margin: "0" },
  radioBtnLabelNavigator: { flex: 1, margin: "0" },
  naviEnabledCheck: {
    width: "100%",
    desplay: "flex",
    justifyContent: "flex-end",
    paddingRight: "50px"
  },
  iconSettingBtn: {
    width: 160,
    borderRadius: 20,
    borderWidth: 2,
    fontFamily: "mplus-1c-regular",
    color: colors.farm_black,
    backgroundColor: colors.farm_white,
    "&:hover": {
      backgroundColor: colors.farm_white,
    },
  }
}
